import React from "react";
import { WEBINAR_TIMEZONE } from "../../constants";
import { WebinarHost, WebinarTopic } from "../../types/models";
import { SessionFormState } from "../../utils/sessionFormState";
import { deriveStartAt } from "../../utils/deriveStartAt";
import "./SessionCardPreview.scss";

type Props = {
  form: SessionFormState;
  host?: WebinarHost;
  topic?: WebinarTopic;
};

const SessionCardPreview: React.FC<Props> = ({ form, host, topic }) => {
  const { start } = deriveStartAt(form.date, form.time);
  const isRescheduled =
    form.currentStatus === "scheduled" &&
    typeof form.origStart === "number" &&
    Number.isFinite(start) &&
    start !== form.origStart;
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: WEBINAR_TIMEZONE,
    month: "short",
    day: "numeric",
    weekday: "short",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).formatToParts(Number.isFinite(start) ? new Date(start) : new Date());
  const get = (type: string) =>
    parts.find((p) => p.type === type)?.value || "";

  const timeLine = `${get("hour")}:${get("minute")} ${get("dayPeriod")} · ${
    form.mins || 45
  } min`;

  return (
    <div className="wf-preview">
      <div className="wf-preview__shell">
        <div className="wf-preview__label">HOW MEMBERS WILL SEE IT</div>
        <div className="wf-preview__card">
          <div className="wf-preview__top">
            <div className="wf-preview__date">
              <div className="mon">{get("month").toUpperCase()}</div>
              <div className="day">{get("day")}</div>
              <div className="dow">{get("weekday").toUpperCase()}</div>
            </div>
            <div className="wf-preview__copy">
              <div className="wf-preview__chips">
                <span
                  className="chip"
                  style={{
                    background: topic?.bg || "#F5F1EC",
                    color: topic?.fg || "#5C554E",
                  }}
                >
                  {topic?.name || "TOPIC"}
                </span>
                {form.access === "members" ? (
                  <span className="chip members">MEMBERS</span>
                ) : null}
                {isRescheduled ? (
                  <span className="chip rescheduled">RESCHEDULED</span>
                ) : null}
              </div>
              <div className="title">
                {form.title.trim() || "Your session title appears here"}
              </div>
              <div className="oneliner">
                {form.oneLiner.trim() ||
                  "The one-line description sits here on the card."}
              </div>
            </div>
          </div>
          <div className="wf-preview__host">
            <div className="wf-avatar sm">
              {host?.photo ? (
                <img src={host.photo} alt="" />
              ) : (
                <span>
                  {(host?.name || "H")
                    .split(/\s+/)
                    .slice(0, 2)
                    .map((p) => p[0]?.toUpperCase() || "")
                    .join("")}
                </span>
              )}
            </div>
            <span className="name">{host?.name || "Host not set"}</span>
            <span className="dot" />
            <span className="time">{timeLine}</span>
          </div>
          <div className="wf-preview__bio">
            {host?.bio ||
              "The host bio shows here — edit it on the host profile."}
          </div>
          {host?.creds?.length ? (
            <div className="wf-preview__creds">
              {host.creds.map((c) => (
                <span key={c}>{c}</span>
              ))}
            </div>
          ) : null}
        </div>
        <div className="wf-preview__note">
          Also renders on the detail hero, the calendar invite and the push
          notification.
        </div>
      </div>
    </div>
  );
};

export default SessionCardPreview;
